import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useMemo, useState } from 'react';
import { PocKey, pocApis } from '../admin.api';
import ResourceManager, { ResourceColumn, ResourceField } from '../components/ResourceManager';

interface PocConfig {
  key: PocKey;
  title: string;
  entityName: string;
  columns: ResourceColumn[];
  fields: ResourceField[];
}

const idColumn: ResourceColumn = {
  id: 'id',
  dataKey: 'id',
  label: 'ID',
  align: 'center',
  sortable: true,
};

const nameColumn: ResourceColumn = {
  id: 'name',
  dataKey: 'name',
  label: 'Name',
  align: 'left',
  sortable: true,
};

/** mobile/phone/whatsapp/email — the contact block four of the five tables share. */
const contactColumns: ResourceColumn[] = [
  { id: 'mobile', dataKey: 'mobile', label: 'Mobile', align: 'left' },
  { id: 'phone', dataKey: 'phone', label: 'Phone', align: 'left' },
  { id: 'whatsapp', dataKey: 'whatsapp', label: 'WhatsApp', align: 'left' },
  { id: 'email', dataKey: 'email', label: 'Email', align: 'left' },
];

const contactFields: ResourceField[] = [
  { name: 'mobile', label: 'Mobile' },
  { name: 'phone', label: 'Phone' },
  { name: 'whatsapp', label: 'WhatsApp' },
  { name: 'email', label: 'Email' },
];

const nameField: ResourceField = { name: 'name', label: 'Name', required: true };

const POCS: PocConfig[] = [
  {
    key: 'clients',
    title: 'CLIENTS',
    entityName: 'Client',
    columns: [
      idColumn,
      nameColumn,
      { id: 'companyName', dataKey: 'companyName', label: 'Company', align: 'left', sortable: true },
      ...contactColumns,
      { id: 'gstNumber', dataKey: 'gstNumber', label: 'GST No.', align: 'left' },
    ],
    fields: [
      nameField,
      { name: 'company_name', label: 'Company Name' },
      ...contactFields,
      { name: 'office_address', label: 'Office Address', type: 'textarea' },
      { name: 'gst_number', label: 'GST Number' },
      { name: 'work_order', label: 'Work Order' },
    ],
  },
  {
    key: 'architects',
    title: 'ARCHITECTS',
    entityName: 'Architect',
    columns: [
      idColumn,
      nameColumn,
      ...contactColumns,
      { id: 'address', dataKey: 'address', label: 'Address', align: 'left' },
    ],
    fields: [nameField, ...contactFields, { name: 'address', label: 'Address', type: 'textarea' }],
  },
  {
    key: 'builders',
    title: 'BUILDERS',
    entityName: 'Builder',
    columns: [
      idColumn,
      nameColumn,
      ...contactColumns,
      { id: 'address', dataKey: 'address', label: 'Address', align: 'left' },
    ],
    fields: [nameField, ...contactFields, { name: 'address', label: 'Address', type: 'textarea' }],
  },
  {
    key: 'owners',
    title: 'OWNERS',
    entityName: 'Owner',
    columns: [
      idColumn,
      nameColumn,
      {
        id: 'buildingName',
        dataKey: 'buildingName',
        label: 'Building',
        align: 'left',
        sortable: true,
      },
      ...contactColumns,
      { id: 'officeAddress', dataKey: 'officeAddress', label: 'Office Address', align: 'left' },
    ],
    fields: [
      nameField,
      { name: 'building_name', label: 'Building Name' },
      ...contactFields,
      { name: 'office_address', label: 'Office Address', type: 'textarea' },
    ],
  },
  {
    key: 'supervisors',
    title: 'SUPERVISORS',
    entityName: 'Supervisor',
    // `supervisors` only carries name + a single free-text contact column.
    columns: [
      idColumn,
      nameColumn,
      { id: 'contact', dataKey: 'contact', label: 'Contact', align: 'left' },
    ],
    fields: [nameField, { name: 'contact', label: 'Contact' }],
  },
];

/**
 * One screen over the five point-of-contact master tables `PocContactController`
 * exposes (clients, architects, builders, owners, supervisors). Same CRUD contract
 * as the reference-data lookups, so the tabs share `ResourceManager`.
 */
export default function ContactMastersPage() {
  const [tab, setTab] = useState(0);
  const active = POCS[tab];
  const client = useMemo(() => pocApis[active.key], [active.key]);

  return (
    <Stack gap={3}>
      <Tabs
        value={tab}
        onChange={(_e, value: number) => setTab(value)}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
      >
        {POCS.map((poc) => (
          <Tab key={poc.key} label={poc.entityName} />
        ))}
      </Tabs>

      <ResourceManager
        // Remount on tab change so list state and form defaults reset cleanly.
        key={active.key}
        title={active.title}
        entityName={active.entityName}
        client={client}
        columns={active.columns}
        fields={active.fields}
      />
    </Stack>
  );
}
